import * as React from 'react';
import './Stores.scss';

import { StoreService } from 'services/ApiService';
import { ChannelService } from 'services/ApiService';
import Select from 'react-select';

import { Store, Channel, ToastState, UserType } from 'types';
import ReactTable, { ReactTableDefaults } from 'react-table';

import { IoEdit, IoTrashB, IoPlus } from 'react-icons/lib/io';
import Modal from 'components/Modal';
import { LoadHolder } from 'components/Loader';
import Tooltip from 'components/Tooltip';
import Toast from 'components/Toast';
import { SearchFilter, TooltipCell, TooltipHeader } from 'helpers/Table';

const StoreAccColumns = (deleteStore: Function, editStore: Function) => [
  {
    id: 'editAcc',
    Header: <TooltipHeader value="Edit Account" />,
    filterable: false,
    sortable: false,
    accessor: props => {
      return(
        <button
          className="tableBtn"
          onClick={() => editStore(props.id)}
        >
          <span><IoEdit /></span>
        </button>
      );
    }
  },
  {
    id: 'deleteAcc',
    Header: <TooltipHeader value="Delete Account" />,
    filterable: false,
    sortable: false,
    accessor: props => {
      const adCount = Number(props.adjustmentCount);
      const refCount = Number(props.refundCount);
      const usCount = Number(props.userCount);
      return (
        <Tooltip
          // English is a bad language!!!
          text={(adCount || refCount || usCount) ? (((adCount || refCount) ?
                  ((adCount + refCount) +
                  ' claim' + (adCount + refCount === 1 ? '' : 's') +
                  (props.userCount !== '0' ? ' and ' : '')) : '') +
                (usCount ?
                  usCount +
                  ' user' + (usCount === 1 ? '' : 's') : '') +
                ' ' + (adCount + refCount + usCount === 1 ? 'is' : 'are') +
                ' assigned to this store') : undefined}
        >
          <span className="button-wrapper">
            <button
              disabled={!!adCount || !!refCount || !!usCount}
              className="tableBtn"
              onClick={() => deleteStore(props.id)}
            >
              <span><IoTrashB /></span>
            </button>
          </span>
        </Tooltip>
      );
    }
  }
];

const ChannelAccColumns = (deleteChannel: Function, editChannel: Function) => [
  {
    id: 'editAcc',
    Header: <TooltipHeader value="Edit Channel" />,
    filterable: false,
    sortable: false,
    accessor: props => {
      return(
        <button
          className="tableBtn"
          onClick={() => editChannel(props.id, props.channelName)}
        >
          <span><IoEdit /></span>
        </button>
      );
    }
  },
  {
    id: 'deleteAcc',
    Header: <TooltipHeader value="Delete Channel" />,
    filterable: false,
    sortable: false,
    accessor: props => {
      const storeCount = Number(props.storeCount);
      return (
        <Tooltip
          text={(storeCount) ? storeCount + ' store(s) are assigned to this channel' : undefined}
        >
          <span className="button-wrapper">
            <button
              disabled={!!storeCount}
              className="tableBtn"
              onClick={() => deleteChannel(props.id)}
            >
              <span><IoTrashB /></span>
            </button>
          </span>
        </Tooltip>
      );
    }
  }
];

const storeColumns = (deleteStore, editStore) => [
  {
    Header: <TooltipHeader value="Op. Number" />,
    accessor: 'storeOperatorNumber',
    Filter: SearchFilter
  }, {
    Header: <TooltipHeader value="Op. Name" />,
    accessor: 'storeOperatorName',
    Filter: SearchFilter,
    filterMethod: (filter, row) => {
      return row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
    }
  }, {
    Header: <TooltipHeader value="Address" />,
    accessor: 'address',
    Filter: SearchFilter,
    filterMethod: (filter, row) => {
      return row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
    }
  }, {
    Header: <TooltipHeader value="Phone #" />,
    accessor: 'phoneNumber',
    Filter: SearchFilter
  }, {
    Header: <TooltipHeader value="Channel" />,
    accessor: 'channelName',
    Filter: SearchFilter,
    filterMethod: (filter, row) => {
      return row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
    }
  },
  ...StoreAccColumns(deleteStore, editStore)
];

const channelColumns = (deleteChannel, editChannel) => [
  {
    Header: <TooltipHeader value="Channel #" />,
    accessor: 'id',
    Filter: SearchFilter,
  },
  {
    Header: <TooltipHeader value="Channel Name" />,
    accessor: 'channelName',
    Filter: SearchFilter,
    filterMethod: (filter, row) => {
      return row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
    },
  },
  ...ChannelAccColumns(deleteChannel, editChannel),
];

type StoresState = {stores: Store[], channels: Channel[], openStore: Store | null, 
  loading: boolean, openChannel: boolean, channelOptions: { value: number, label: string }[], 
  channelInput: Channel | null, openEditChannel: boolean};

export default class Stores extends React.Component<{}, StoresState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      stores: [],
      channels: [],
      openStore: null,
      loading: false,
      openChannel: false,
      channelOptions: [],
      channelInput: null,
      openEditChannel: false
    };
  }
  reload = async () => {
    this.setState({loading: true});
    let stores = await StoreService.fetchAll();
    let channels = await ChannelService.fetchAll();
    let channelOptions = [{ value: -1, label: 'No Channel'}].concat(channels.map(channel => ({
      value: channel.id,
      label: channel.channelName
    })));

    if (stores.length > 0) {
      let objOfChannels = {};
      for (let channel of channels) {
        objOfChannels[channel.id] =  {
          channelName: channel.channelName
        };
      }
      stores.map(store => {
        if (store.channelId && Object.keys(objOfChannels).length !== 0) {
          store.channelName = objOfChannels[store.channelId].channelName;
        } else {
          store.channelName = '-';
        }
      });
    }
    
    this.setState({loading: false, stores: stores, channels: channels, channelOptions: channelOptions});
  }
  openEditStorePopup = (id: number) => {
    this.setState({openStore: Object.assign({}, this.state.stores.find(store => store.id === id)!!)});
  }
  createStore = () => {
    this.setState({
      openStore: { id: -1 } as Store
    });
  }
  deleteStore = (id: number) => {
    if (!confirm('Delete store?')) return;
    this.setState({loading: true}, () => {
      StoreService.delete(id).then(deletedStore => {
        const stores = this.state.stores;
        const index = stores.findIndex(store => store.id === id);
        stores.splice(index, 1);
        Toast.present({ message: 'Store deleted successfully' }, ToastState.SUCCESS);
        this.setState({stores, loading: false});
      }, reject => {
        this.setState({loading: false});
      });
    });
  }
  saveStore = () => {
    this.setState({loading: true}, () => {
      const isNewStore = this.state.openStore!!.id === -1 ? true : false;
      if (this.state.openStore!.channelId === -1) {
        this.state.openStore!.channelId = null;
      }
      StoreService[isNewStore ? 'create' : 'patch'](this.state.openStore!!).then(newStore => {
        Toast.present({
          message: 'Store ' + (isNewStore ? 'created' : 'updated') + ' successfully'
        }, ToastState.SUCCESS);
        this.reload();
        this.setState({openStore: null});
      }, reject => {
        this.setState({loading: false});
      });
    });
  }
  openChannelPopup = () => {
    this.setState({
      channelInput: { id: -1, channelName: '' } as Channel,
      openChannel: true,
    });
  }
  openEditChannelPopup = (id: number, channelName: string) => {
    this.setState({
      channelInput: { id: id, channelName: channelName, users: [], storesList: '' },
      openEditChannel: true,
    });
  }

  addChannel = (newChannel: boolean) => {
    const { channels, channelInput, channelOptions } = this.state;
    this.setState({loading: true}, () => {
      channelInput!.channelName = channelInput!.channelName.trim();
      let currentChannelNames = this.state.channels.map(data => data.channelName);
      if (channelInput!.channelName !== '') {
        if (currentChannelNames.indexOf(channelInput!.channelName) > -1 ) {
          Toast.present({
            message: 'That Channel Name already exists'
          }, ToastState.ERROR);
          this.setState({ channelInput: { id: -1, channelName: '' } as Channel, loading: false });
        } else {
          if (newChannel) {
            ChannelService.create(channelInput!).then((data) => {
              channelOptions.push({ value: data.id, label: channelInput!.channelName});
              channels.push(data);
              Toast.present({
                message: 'Channel was successfully created.'
              }, ToastState.SUCCESS);
              this.setState({ channelInput: { id: -1, channelName: '' } as Channel, loading: false });
            }, reject => {
              Toast.present({
                message: 'Failed to create channel.'
              }, ToastState.ERROR);
              this.setState({loading: false});
            });
          } else {
            ChannelService.patch(channelInput!).then(() => {
              let channelsIndex = channels.findIndex(channel => channel.id === channelInput!.id);
              channels[channelsIndex] = channelInput!;
  
              let channelOptionsIndex = channelOptions.findIndex(channel => channel.value === channelInput!.id);
              channelOptions[channelOptionsIndex] = { value: channelInput!.id, label: channelInput!.channelName};
              Toast.present({
                message: 'Channel was successfully renamed.'
              }, ToastState.SUCCESS);
  
              this.setState({ channelInput: { id: -1, channelName: '' } as Channel, loading: false});
              this.closeEditChannelModal();
            }, reject => {
              Toast.present({
                message: 'Failed to create channel.'
              }, ToastState.ERROR);
              this.setState({loading: false});
            });
          }
        }
      } else {
        Toast.present({
            message: 'Channel name is blank.'
          }, ToastState.ERROR);
        this.setState({loading: false});
      }
    });
  }

  deleteChannel = (id: number) => {
    if (!confirm('Delete Channel #' + id + '?')) return;

    ChannelService.fetch(id).then(channelInfo => {
      let users = channelInfo.users;
      let ifValidUser: boolean = false;
      if (users.length >= 1 ) {
        let ctrl = 0;
        let seperator: string;
        let userList: string = '';
        let maxUserList = 4;
        for (let user of users) {
          if (user.type !== UserType.Inactive) {
            ifValidUser = true;
            if (ctrl > maxUserList) {
              userList += '...';
              break;
            }
            seperator = ctrl ? ', ' : '';
            userList = userList + seperator + user.contactName;
            ctrl++;
          }
        }
        if (ifValidUser) {
          Toast.present({ message: 'Can not delete channel since it is assigned to users: ' + userList}, 
          ToastState.ERROR);
        }
      }
      if (!ifValidUser) {
        this.setState({loading: true}, () => {
          ChannelService.delete(id).then(deletedChannel => {
            const channels = this.state.channels;
            const channelOptions = this.state.channelOptions;
    
            const channelsIndex = channels.findIndex(store => store.id === id);
            channels.splice(channelsIndex, 1);
            
            const channelOptionsIndex = channelOptions.findIndex(channel => channel.value === id);
            channelOptions.splice(channelOptionsIndex, 1);
    
            Toast.present({ message: 'Channel deleted successfully' }, ToastState.SUCCESS);
            this.setState({channels, loading: false});
          }, reject => {
            this.setState({loading: false});
          });
        });
      }
    });
  }

  onChange = (property: string, e) => {
    const { openStore } = this.state;
    const element = e === null ? '' : e;
    if (property !== 'channel') {
      openStore!![property] = element.target.value;
    } else {
      openStore!!.channelId = Number(element.value);
    }
    this.setState({
      openStore
    });
  }

  onAddChannelChange = (e) => {
    const { channelInput } = this.state;
    channelInput!.channelName = String(e.target.value);
    this.setState({channelInput});
  }

  closeStoreModal = () => {
    this.setState({openStore: null});
  }
  closeChannelModal = () => {
    this.setState({channelInput: { id: -1, channelName: '' } as Channel});
    this.setState({openChannel: false});
  }
  closeEditChannelModal = () => {
    this.setState({channelInput: { id: -1, channelName: '' } as Channel});
    this.setState({openEditChannel: false});
  }
  componentDidMount() {
    this.reload();
  }
  render() {
    const { stores, channels, loading, openStore, openChannel, openEditChannel, 
      channelOptions, channelInput } = this.state;
    return (
      <div className="stores">
        <h1>Stores</h1>
        <button className="button" onClick={this.createStore}>
          Create New Store
        </button>
        <button className="button" onClick={this.openChannelPopup}>
          Manage Store Channels
        </button>
        <ReactTable
          loading={loading}
          minRows={0}
          filterable={true}
          data={stores}
          columns={storeColumns(this.deleteStore, this.openEditStorePopup)}
          column={{
            ...ReactTableDefaults.column,
            Cell: TooltipCell
          }}
        />
        <Modal visible={!!openStore} onClose={this.closeStoreModal}>
          {openStore ? <div className="store-modal">
            <h2>{openStore.id === -1 ? 'Create' : 'Edit'} Store #{openStore.storeOperatorNumber}</h2>
            <label>Store Channel</label>
            <Select
                    disabled={loading}
                    options={channelOptions}
                    onChange={this.onChange.bind(this, 'channel')}
                    value={openStore.channelId}
                    isSearchable={true}
                    placeholder="No Channel"
                    defaultValue={null}
                    className="SelectDrop"
            />
            <button className="tableBtn" id="createButton" onClick={() => this.openChannelPopup()}>
              <span><IoPlus /></span></button>
            <div className="bigger-gap"/>
            <label>Store Operator Number</label>
            <input
              type="number"
              disabled={loading}
              placeholder="Store Operator Number"
              onChange={this.onChange.bind(this, 'storeOperatorNumber')}
              value={openStore.storeOperatorNumber}
            />
            <label>Store Operator Name</label>
            <input
              disabled={loading}
              placeholder="Store Operator Name"
              onChange={this.onChange.bind(this, 'storeOperatorName')}
              value={openStore.storeOperatorName}
            />
            <label>Address</label>
            <input
              disabled={loading}
              placeholder="Store Address"
              onChange={this.onChange.bind(this, 'address')}
              value={openStore.address}
            />
            <label>City</label>
            <input
              disabled={loading}
              placeholder="Store City"
              onChange={this.onChange.bind(this, 'city')}
              value={openStore.city}
            />
            <label>Postal Code</label>
            <input
              disabled={loading}
              placeholder="Store Postal Code"
              onChange={this.onChange.bind(this, 'postalCode')}
              value={openStore.postalCode}
            />
            <label>Phone Number</label>
            <input
              disabled={loading}
              placeholder="Store Phone Number"
              type="tel"
              onChange={this.onChange.bind(this, 'phoneNumber')}
              value={openStore.phoneNumber}
            />
            <button
              disabled={loading}
              className="button"
              onClick={this.saveStore}
            >
              Save Changes
            </button> <LoadHolder loading={loading} />
          </div> : null}
        </Modal>

        <Modal visible={!!openChannel} onClose={this.closeChannelModal}>
          {openChannel && channelInput ? 
          <div className="channel-modal">
          <h2>Manage Store Channels</h2>
          <label>Create a Channel</label>
            <input
              disabled={loading}
              placeholder="Create a Channel"
              value={channelInput.channelName}
              onChange={this.onAddChannelChange}
            />
            <button className="tableBtn" id="createButton" onClick={() => this.addChannel(true)}>
              <span><IoPlus /></span></button>
          <div className="bigger-gap"/>
          <label>Edit Channels</label>
          <div className="tiny-gap"/>
          <ReactTable
            loading={loading}                
            minRows={0}
            filterable={true}
            data={channels}
            columns={channelColumns(this.deleteChannel, this.openEditChannelPopup)}
            defaultPageSize={10}
            showPageSizeOptions={false}
            column={{
              ...ReactTableDefaults.column,
              Cell: TooltipCell
            }}
          />
          </div> : null}
        </Modal>

        <Modal visible={!!openEditChannel} onClose={this.closeEditChannelModal}>
          {openEditChannel ? 
          <div className="channel-modal">
          <h2>Edit Channel Name</h2>
          <label>Rename Channel #{channelInput!.id}</label>
          <input
            disabled={loading}
            placeholder={channelInput!.channelName}
            onChange={this.onAddChannelChange}
            value={channelInput!.channelName}
          />
          <button className="tableBtn" id="createButton" onClick={() => this.addChannel(false)}>
              <span>Save</span></button>
          </div> : null}
        </Modal>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/Stores/index.tsx